const API_BASE = process.env.REACT_APP_API_BASE_URL;

const companyBasedRequest = {
	async updateShopServices(shopId, adminId, services, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/services`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({
				adminId,
				service: services,
			}),
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to update shop services: ${text}`);
		}

		return res.json();
	},

	async updateShopCategory(shopId, category, subCategory, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/category`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({
				category,
				subCategory,
			}),
		});
	
		if (!res.ok) {
			throw new Error(await res.text());
		}
	
		return res.json(); // { message, shop }
	},	

	async updateBookingTransitAdmin(bookingId, actualTransitMinutes, accessToken) {
		const res = await fetch(
			`${API_BASE}/admin/bookings/${bookingId}/transit`,
			{
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${accessToken}`,
				},
				body: JSON.stringify({ actualTransitMinutes }),
			}
		);
	
		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async updateManualBookingAdmin(bookingId, data, token) {
		const res = await fetch(`${API_BASE}/admin/bookings/${bookingId}/manual`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(data),
		});
	
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error || "Failed to update manual booking");
		}
	
		return res.json();
	},

	async updateShopServiceCategories(shopId, categories, accessToken) {
		const res = await fetch(
			`${API_BASE}/shops/${shopId}/service-categories`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${accessToken}`,
				},
				body: JSON.stringify({ categories }),
			}
		);
	
		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to update service categories: ${text}`);
		}
	
		return res.json(); // { message, serviceCategories }
	},
	
	async updateShopAttributes(shopId, attributes, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/attributes`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ attributes }),
		});
		if (!res.ok) throw new Error("Failed to update shop service");
		return res.json();
	},

	async removeShopService(shopId, serviceId, adminId, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/services/${serviceId}`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ adminId }),
		});
		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async removeStaff(shopId, staffId, email, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/remove-staff`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ shopId, staffId, email }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async promoteStaff(shopId, targetUserId, actingUserId, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/promote-staff`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ targetUserId, actingUserId }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async demoteStaff(shopId, targetUserId, actingUserId, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/demote-staff`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ targetUserId, actingUserId }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async updateUserServices(userId, serviceIds, accessToken) {
		const res = await fetch(`${API_BASE}/users/${userId}/services`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ serviceIds }), // CORRECT
		});
		if (!res.ok) throw new Error("Failed to update user services");
		return res.json();
	},
	
	async updateShopInfo(shopId, updateData, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/info`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(updateData),
		});

		if (!res.ok) {
			throw new Error(await res.text());
		}

		return res.json();
	},

	async updateShopContactInfo(shopId, updateData, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/contact`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(updateData),
		});
		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async updateShopHours(shopId, hoursData, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/hours`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(hoursData),
		});
		if (!res.ok) throw new Error("Failed to update shop hours");
		return res.json();
	},

	async uploadShopImage({ file, fileName, fileType, shopId, accessToken }) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/images`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({
				file,
				fileName,
				fileType,
				shopId,
			}),
		});

		if (!res.ok) {
			throw new Error(await res.text());
		}

		return res.json();
	},

	async updateShopImages({ shopId, images, accessToken }) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/images`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ images }),
		});

		if (!res.ok) {
			throw new Error(await res.text());
		}

		return res.json();
	},

	async getOnboardingStatus(shopId) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/onboarding`);
		if (!res.ok) throw new Error("Failed to get onboarding status");
		return res.json();
	},

	async launchShop(shopId, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/launch`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // ✅ attach token
			},
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to launch shop: ${text}`);
		}

		return res.json();
	},

	async launchStaff(staffId, accessToken) {
		const res = await fetch(`${API_BASE}/staff/${staffId}/launch`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to launch staff: ${text}`);
		}

		return res.json();
	},

	async invitestaff(shopId, email, firstName, lastName, accessToken) {
		const payload = Array.isArray(email)
			? {
					email,
					firstName: Array.isArray(firstName) ? firstName : [],
					lastName: Array.isArray(lastName) ? lastName : [],
			  }
			: { email, firstName, lastName };

		const res = await fetch(`${API_BASE}/shops/${shopId}/invite-staff`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(payload),
		});

		if (!res.ok) throw new Error("Failed to send staff invite");
		return res.json();
	},
	
	async verifyInvite(token) {
		const res = await fetch(`${API_BASE}/invites/verify?token=${encodeURIComponent(token)}`);
		if (!res.ok) throw new Error("Invalid or expired invite");
		return res.json();
	},

	async reinviteStaff(shopId, email, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/reinvite-staff`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ email }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async cancelInvite(shopId, email, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/cancel-invite`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // required since route is protected
			},
			body: JSON.stringify({ email }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async requestShopInvite(formData) {
		const res = await fetch(`${API_BASE}/shops/apply`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(formData),
		});
		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async requestNewCategory(formData) {
		const res = await fetch(`${API_BASE}/shops/request-category`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(formData),
		});
	
		if (!res.ok) {
			const text = await res.text();
			throw new Error(text);
		}
	
		return res.json();
	},

	async verifyShopInvite(token) {
		const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/verify-shop-invite?token=${token}`);
		if (!res.ok) throw new Error("Invalid invite");
		return res.json(); // should return { valid: true, email, shopName }
	},

	async getStaffWeekSchedule(staffId, date, accessToken) {
		if (!staffId) throw new Error("Missing staffId");
		if (!accessToken) throw new Error("Missing accessToken");

		let url = `${API_BASE}/staff/${staffId}/week-schedule`;
		if (date) url += `?date=${encodeURIComponent(date)}`;

		const res = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // ✅ add token
			},
		});

		if (!res.ok) throw new Error("Failed to fetch weekly schedule");
		return res.json();
	},

	async getShopWeekSchedule(shopId, date, accessToken) {
		if (!shopId) throw new Error("Missing staffId");
		if (!accessToken) throw new Error("Missing accessToken");

		let url = `${API_BASE}/shop/${shopId}/week-schedule`;
		if (date) url += `?date=${encodeURIComponent(date)}`;

		const res = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // ✅ add token
			},
		});

		if (!res.ok) throw new Error("Failed to fetch weekly schedule");
		return res.json();
	},

	async registerAdmin(data) {
		const res = await fetch(`${API_BASE}/admin/register`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(data),
		});
		if (!res.ok) throw new Error("Failed to register admin");
		return res.json();
	},

	async registerStaff(data) {
		// data = { token, email, password, firstName, lastName, phoneNumber, image }
		const res = await fetch(`${API_BASE}/staff/register-with-invite`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(data),
		});
		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async getShopServices(shopId, accessToken) {
		const res = await fetch(`${API_BASE}/shops/${shopId}/services`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) throw new Error("Failed to fetch shop services");
		return res.json(); // { services: [...] }
	},

	async getAllAttributes() {
		const res = await fetch(`${API_BASE}/attributes`);
		if (!res.ok) throw new Error("Failed to get attributes");
		return res.json();
	},

	async updateStaffInfo(userId, data, accessToken) {
		const res = await fetch(`${API_BASE}/users/${userId}/info`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // ✅ include token
			},
			body: JSON.stringify(data),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json(); // { message, user }
	},

	async cancelBookingAdmin(bookingId, accessToken, role) {
		const res = await fetch(`${API_BASE}/admin/bookings/${bookingId}/cancel`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ role }), // 👈 send user role
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async cancelBookingStaff(bookingId, accessToken, role) {
		const res = await fetch(`${API_BASE}/staff/bookings/${bookingId}/cancel`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ role }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},


	async reassignBookingAdmin(bookingId, newStaffId, accessToken) {
		const res = await fetch(`${API_BASE}/admin/bookings/${bookingId}/reassign`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ newStaffId }),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},
	async getStaffInfoPrivate(staffId, accessToken) {
		const res = await fetch(`${API_BASE}/staff/${staffId}/private`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // must include token
			},
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json(); // { staff: {...} }
	},

	async getShopWithstaffsByIdPrivate(shopId, accessToken) {
		const res = await fetch(`${API_BASE}/shopwithstaffsprivate/${shopId}`, {
			method: "GET",
			headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
		});

		if (!res.ok) throw new Error("Failed to fetch shop");
		return res.json();
	},

	async uploadProfilePhoto(shopId, userId, file, fileType, accessToken) {
		const res = await fetch(`${API_BASE}/users/${userId}/profile-photo`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({
				shopId,
				userId, // REQUIRED
				file,
				fileType,
			}),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json();
	},

	async manualBooking(data, accessToken) {
		const res = await fetch(`${API_BASE}/manual-booking`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`, // 🔐 protect route
			},
			body: JSON.stringify(data),
		});

		if (!res.ok) throw new Error(await res.text());
		return res.json(); // { booking: {...} }
	},
};

export default companyBasedRequest;
